import React, {useEffect, useState} from 'react'
import {InfoCard2} from '../../widgets/components/UI/InfoCard2'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../../_metronic/layout/core'
import {CaptableManagementBreadCrumbs} from '../core/_constants'
import {DataTableServerSideFinancial} from './DataTableServerSideFinancial'
import brandingConfig from '../../../../config/brandingConfig'
import {useParams} from 'react-router-dom'
import {useAuth} from '../../auth'
import {getScenario} from '../core/_requests'
import {toast} from 'react-toastify'
import FinancialDetailsSkeleton from './FinancialDetailsSkeleton'
import {CaptableSkeleton} from './CaptableSkeleton'

interface Captable {
  id: number
  shareholderId: string
  shareholdings: string
  designation: string
  security_class: string[]
  phone: string
  transactions: number
  fully_diluted_shares: number
  amount_invested: number
  ownership: string
  ownership_diff: number
  alltransactions: any[]
  profileImage: {
    id: string
    name: string
  } | null
}

const FinancialDetails = () => {
  const {scenarioId} = useParams()
  const {companyId} = useAuth()
  const [isloading, setIsloading] = useState<boolean>(false)
  const [scenario, setScenario] = useState<any>()
  const {formatMessage} = useIntl()
  const [subtotals, setSubtotals] = useState({
    totalFullyDilutedShares: 0,
    totalAmountInvested: 0,
    totalTransactionCount: 0,
  })
  const [shareholders, setShareholders] = useState<Captable[]>()

  const getScenarioDetails = async () => {
    if (!companyId) return
    setIsloading(true)
    try {
      const {
        data: {data, success, errors},
      } = await getScenario(companyId, scenarioId!)
      if (success) {
        setScenario(data)
        let totalTransactionCount = 0
        const totalFullyDilutedShares = data.allShareholdersDetails.reduce(
          (sum: number, shareholder: any) => sum + (shareholder.investorFullyDilutedShares || 0),
          0
        )

        const totalAmountInvested = data.allShareholdersDetails.reduce(
          (sum: number, shareholder: any) => sum + (shareholder.totalShareValue || 0),
          0
        )

        const transformedData = data.allShareholdersDetails.map(
          (shareholder: any, index: number) => {
            const shareTransactions = shareholder.shareTransactions.map((sc: any) => ({
              transactionId: sc.shareTransactionId,
              numberOfShares: sc.numberOfShares,
              pricePerShare: sc.pricePerShare,
              totalTransactionValue: sc.totalTransactionValue,
              className: sc.securityClass.className,
              classCode: sc.securityClass.classCode,
              tagColor: sc.securityClass.tagColor,
              type: 'share',
              createdAt: sc.createdAt || '',
            }))

            const convertibleTransactions = shareholder.convertibleTransactions.map((ct: any) => ({
              transactionId: ct.convertibleTransactionId,
              investedAmount: ct.investedAmount,
              className: ct.securityClass.className,
              classCode: ct.securityClass.classCode,
              tagColor: ct.securityClass.tagColor,
              type: 'convertible',
              createdAt: ct.createdAt || '',
            }))

            const warrantTransactions = shareholder.warrantTransactions.map((wt: any) => ({
              transactionId: wt.warrantTransactionId,
              warrantIssued: wt.warrantIssued,
              amountPaid: wt.amountPaid,
              className: wt.securityClass.className,
              classCode: wt.securityClass.classCode,
              tagColor: wt.securityClass.tagColor,
              type: 'warrant',
              createdAt: wt.createdAt || '',
            }))

            // Combine all transactions
            const allTransactions = [
              ...shareTransactions,
              ...convertibleTransactions,
              ...warrantTransactions,
            ]

            totalTransactionCount += allTransactions.length
            return {
              id: index + 1,
              shareholderId: shareholder.shareholderId,
              shareholdings: shareholder.legalName
                ? shareholder.legalName
                : `${shareholder.firstName} ${shareholder.lastName || ''}`.trim(),
              designation: '',
              security_class: Array.from(
                new Set([
                  ...shareholder.shareTransactions.map((cls: any) =>
                    JSON.stringify({
                      classCode: cls.securityClass.classCode,
                      tagColor: cls.securityClass.tagColor,
                    })
                  ),
                  ...shareholder.warrantTransactions.map((wt: any) =>
                    JSON.stringify({
                      classCode: wt.securityClass.classCode,
                      tagColor: wt.securityClass.tagColor,
                    })
                  ),
                  ...shareholder.convertibleTransactions.map((ct: any) =>
                    JSON.stringify({
                      classCode: ct.securityClass.classCode,
                      tagColor: ct.securityClass.tagColor,
                    })
                  ),
                ])
              ).map((item: string) => JSON.parse(item)),
              phone: 'N/A',
              transactions: allTransactions.length,
              fully_diluted_shares: shareholder.investorFullyDilutedShares,
              amount_invested: shareholder.totalShareValue,
              ownership: shareholder.ownership,
              ownership_diff: 0,
              alltransactions: allTransactions,
              profileImage: {
                id: shareholder.profileImgId || '',
                name: shareholder.profileImgName || '',
              },
            }
          }
        )
        const scenarioShareholderIds = data.scenario.shareholderNewInvestments.map(
          (investment: any) => investment.shareholderId
        )

        const filteredShareholders = transformedData.filter((shareholder: any) =>
          scenarioShareholderIds.includes(shareholder.shareholderId)
        )
        setShareholders(filteredShareholders)
        setSubtotals({
          totalTransactionCount,
          totalFullyDilutedShares,
          totalAmountInvested,
        })
      } else {
        toast.error(errors[0])
      }
    } catch (error) {
      console.error('Error ', error)
    } finally {
      setIsloading(false)
    }
  }

  useEffect(() => {
    if (scenarioId) {
      getScenarioDetails()
    }
  }, [scenarioId]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className='px-6'>
      <PageTitle
        breadcrumbs={CaptableManagementBreadCrumbs}
        description={formatMessage({id: 'Performa'})}
      >
        {formatMessage({id: 'Cap Table Management'})}
      </PageTitle>
      <div className='w-100'>
        <InfoCard2
          title={formatMessage({id: 'How to Manage Your Cap Table Efficiently?'})}
          desc={formatMessage({
            id: `${brandingConfig.brandName}’s Cap Table Management feature offers a seamless and organized approach to track your equity, project future ownership scenarios, and analyze management events.`,
          })}
          slug={brandingConfig.helpLinks.captableManagementDashboardOverview}
          className='w-100 mb-0'
        />
      </div>
      {isloading ? (
        <FinancialDetailsSkeleton />
      ) : (
        scenario && (
          <div className='bg-white w-100 border px-3 py-4 rounded mb-4'>
            <div className='d-flex flex-column justify-content-between'>
              <div className='d-flex mb-md-4 flex-column flex-md-row'>
                <div className='d-flex flex-column justify-content-md-start w-100'>
                  <h1 className='font-size-16'>{scenario.scenario.scenarioName}</h1>
                  <p className='font-size-13 text-muted m-0 mb-2'>
                    {formatMessage({id: 'Scenario Name'})}
                  </p>
                </div>
                <div className='d-flex flex-column justify-content-md-start w-100'>
                  <h1 className='font-size-16'>{scenario.scenario.roundName}</h1>
                  <p className='font-size-13 text-muted m-0 mb-2'>
                    {formatMessage({id: 'Round Name'})}
                  </p>
                </div>
                <div className='d-flex flex-column justify-content-md-start w-100'>
                  <h1 className='font-size-16'>{scenario.scenario.preMoneyValuation}</h1>
                  <p className='font-size-13 text-muted m-0 mb-2'>
                    {formatMessage({id: 'Pre Money Valuation'})}
                  </p>
                </div>
                <div className='d-flex flex-column justify-content-md-start w-100'>
                  <h1 className='font-size-16'>
                    {scenario.scenario.scenario_convertibleClassMaps.length > 0 ? 'Yes' : 'No'}
                  </h1>
                  <p className='font-size-13 text-muted m-0 mb-2'>
                    {formatMessage({id: 'Convert Convertible Notes'})}
                  </p>
                </div>
              </div>
              <div className='d-flex flex-column flex-md-row'>
                <div className='d-flex flex-column justify-content-md-start w-100'>
                  <h1 className='font-size-16'>{scenario.existingInvestorTotalInvestment}</h1>
                  <p className='font-size-13 text-muted m-0 mb-2'>
                    {formatMessage({id: 'Invested by Existing Investors'})}
                  </p>
                </div>
                <div className='d-flex flex-column justify-content-md-start w-100'>
                  <h1 className='font-size-16'>{Math.abs(scenario.newInvestorTotalInvestment)}</h1>
                  <p className='font-size-13 text-muted m-0 mb-2'>
                    {formatMessage({id: 'Invested by New Investors'})}
                  </p>
                </div>
                <div className='d-flex flex-column justify-content-md-start w-100'>
                  <h1 className='font-size-16'>{Math.abs(scenario.unallocatedInvestment)}</h1>
                  <p className='font-size-13 text-muted m-0 mb-2'>
                    {formatMessage({id: 'Unallocated'})}
                  </p>
                </div>
                <div className='d-flex flex-column justify-content-md-start w-100'>
                  <h1 className='font-size-16'>
                    {parseFloat(scenario.newInvestorTotalInvestment || 0) +
                      parseFloat(scenario.existingInvestorTotalInvestment || 0)}
                  </h1>
                  <p className='font-size-13 text-muted m-0 mb-2'>
                    {formatMessage({id: 'Total Investment Amount'})}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      <div>
        <h1 className='font-size-16'>{formatMessage({id: 'Pro Forma Capitalization'})}</h1>
        <p className='text-muted'>
          {formatMessage({
            id: 'View proceeds and metrics by share classes and top stakeholders for the exit',
          })}
        </p>
      </div>
      {isloading ? (
        <CaptableSkeleton />
      ) : (
        shareholders && (
          <DataTableServerSideFinancial shareholders={shareholders} subtotals={subtotals} />
        )
      )}
    </div>
  )
}

export default FinancialDetails
